import { useQuery } from 'react-query';
import {
	getHiringApplicantsForPosition,
	getHiringCandidatesForPosition,
	getMyRegisteredPositions,
	getPositionById,
	getPositionDetailsById,
	getPositions,
	getRecommendationStatus,
	getRecommendationsForPosition,
	levelDistribution,
} from 'src/apis/position';
import { useUserContext } from 'src/context/UserProvider';
import {
	FilterPositionCompanyInput,
	FilterRecommendationsInput,
	GetCampusHiringCandidatesAPIResponse,
	GetLevelDistributionDataAPIResponse,
	GetPositionByIdAPIResponse,
	GetPositionUserRecommendationStatusAPIResponse,
	GetPositionsAPIResponse,
	GetPositionsForRecruiterAPIResponse,
	GetRecommendationsForRecruiterAPIResponse,
	UserExperienceLevelType,
	UserFiltersForApis,
} from 'types';

export const useGetPositionsForRecruiterQuery = (
	isAccountManagerLoggedIn: boolean,
	companyId?: string,
	onComplete?: (data: GetPositionsForRecruiterAPIResponse) => void,
	filterQuery?: FilterPositionCompanyInput,
) => {
	const { isRecruiterLoggedIn, setHasPosition } = useUserContext();
	return useQuery({
		queryKey: ['getMyRegisteredPositions', companyId],
		queryFn: async () =>
			(await getMyRegisteredPositions({
				companyId,
				pageUrl: window.location.href,
				...filterQuery,
			})) as GetPositionsForRecruiterAPIResponse,
		staleTime: 0,
		enabled: isRecruiterLoggedIn || (!!companyId && companyId !== 'undefined'),
		onSuccess(data) {
			if (data?.positions && data?.positions?.length > 0) {
				setHasPosition(true);
			}
			onComplete && onComplete(data);
		},
		// refetchOnMount: true,
	});
};

export const useGetPositionUserRecommendationStatus = (
	args: { positionId: string; handler: string },
	onComplete?: (data: GetPositionUserRecommendationStatusAPIResponse) => void,
) => {
	return useQuery({
		queryKey: ['getRecommendationStatus', args.positionId, args.handler],
		queryFn: async () => (await getRecommendationStatus(args)) as GetPositionUserRecommendationStatusAPIResponse,
		staleTime: 0,
		enabled: !!args.positionId && !!args.handler && args.positionId !== 'undefined' && args.handler !== 'undefined',
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		refetchOnMount: true,
	});
};

export const useGetPositionsQuery = (
	isJobseekerLoggedIn: boolean,
	onComplete?: (data: GetPositionsAPIResponse) => void,
	handleError?: () => void,
) => {
	return useQuery({
		queryKey: ['getPositions'],
		queryFn: async () => (await getPositions()) as GetPositionsAPIResponse,
		staleTime: 0,
		refetchOnMount: true,
		enabled: isJobseekerLoggedIn,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useGetPositionByIdQuery = (
	id?: string,
	onComplete?: (data: GetPositionByIdAPIResponse) => void,
	handleError?: () => void,
) => {
	return useQuery({
		queryKey: ['getPositionById', id],
		queryFn: async () => (await getPositionById(id ?? '')) as GetPositionByIdAPIResponse,
		staleTime: -1,
		enabled: !!id && id !== 'undefined',
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useGetPositionDetailsByIdQuery = (
	id?: string,
	onComplete?: (data: GetPositionByIdAPIResponse) => void,
	handleError?: () => void,
) => {
	return useQuery({
		queryKey: ['getPositionDetailsByIdQuery', id],
		queryFn: async () => (await getPositionDetailsById(id ?? '')) as GetPositionByIdAPIResponse,
		staleTime: 0,
		enabled: !!id,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useGetLevelDistributionByIdQuery = (
	id?: string,
	onComplete?: (data: GetLevelDistributionDataAPIResponse) => void,
	handleError?: () => void,
) => {
	return useQuery({
		queryKey: ['getLevelDistributionById', id],
		queryFn: async () => (await levelDistribution(id ?? '')) as GetLevelDistributionDataAPIResponse,
		staleTime: 0,
		enabled: !!id,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useGetRecommendationsForPositionQuery = (
	args: FilterRecommendationsInput & { positionId: string },
	onComplete?: (data: GetRecommendationsForRecruiterAPIResponse) => void,
) => {
	return useQuery({
		queryKey: ['getRecommendationsForPosition', ...Object.values(args)],
		queryFn: async () => (await getRecommendationsForPosition(args)) as GetRecommendationsForRecruiterAPIResponse,
		staleTime: 0,
		refetchInterval: 15 * 1000,
		enabled: !!args.positionId && args.positionId !== 'undefined',
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		refetchOnMount: true,
	});
};

export const useGetHiringCandidatesForPosition = ({
	page,
	limit,
	filters,
	onComplete,
	positionId,
	handleError,
}: {
	page: number;
	limit?: number;
	positionId?: string;
	filters?: UserFiltersForApis;
	onComplete?: (data: GetCampusHiringCandidatesAPIResponse) => void;
	handleError?: () => void;
}) => {
	return useQuery({
		queryKey: ['getCampusHiringCandidates', page, limit, filters, positionId],
		queryFn: async () =>
			(await getHiringCandidatesForPosition({
				page,
				limit,
				positionId,
				...filters,
			})) as GetCampusHiringCandidatesAPIResponse,
		staleTime: 0,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useGetHiringApplicantsForPosition = ({
	page,
	limit,
	filters,
	onComplete,
	positionId,
	handleError,
}: {
	page: number;
	limit?: number;
	positionId: string;
	filters?: UserFiltersForApis;
	onComplete?: (data: GetCampusHiringCandidatesAPIResponse) => void;
	handleError?: () => void;
}) => {
	return useQuery({
		queryKey: ['getAllApplicantsForPosition', page, limit, filters, positionId],
		queryFn: async () =>
			(await getHiringApplicantsForPosition({
				page,
				limit,
				positionId,
				...filters,
			})) as GetCampusHiringCandidatesAPIResponse,
		staleTime: 0,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};
